// src/pages/Onboarding/slides/OptionSelectSlide.js
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import SlideShell from '../SlideShell';
import OptionButton from '../../../components/Onboarding/OptionButton';

const GRID_COLUMN_CLASSNAMES = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
};

export default function OptionSelectSlide({
  title,
  subtitle,
  options = [],
  currentStep,
  totalSteps,
  onNext,
  onBack,
  selectedValue,
  selectedValues,
  gridLayout = false,
  optionIconContainerClass,
  optionIconSizeClass,
  fieldName = null,
  autoAdvance = false,
  animationDirection = 'forward',
  isTransitioning = false,
  multiselect = false,
  minSelections = 1,
  maxSelections = Infinity,
  otherOptionId = null,
  otherOptionsList = [],
  otherPlaceholder = 'Select your country…',
  renderOtherOption = null,
  onAutoSave = null,
  exclusiveOptionIds = [],
  shortViewportBehavior = null,
}) {
  const resolvedFieldName = useMemo(() => {
    if (fieldName) return fieldName;
    const lowerTitle = title?.toLowerCase() ?? '';
    if (lowerTitle.includes('old')) return 'age';
    if (lowerTitle.includes('from') || lowerTitle.includes('country')) return 'country';
    if (lowerTitle.includes('budget')) return 'budget';
    return 'selection';
  }, [fieldName, title]);

  const optionsById = useMemo(() => new Set(options.map((option) => option.id)), [options]);
  const exclusiveSet = useMemo(() => new Set(exclusiveOptionIds || []), [exclusiveOptionIds]);
  const shortViewportEnabled = Boolean(shortViewportBehavior?.enabled);
  const shortViewportBreakpoint = shortViewportBehavior?.breakpoint ?? 600;
  const shortViewportColumns = shortViewportBehavior?.compactGridColumns ?? 3;
  const shortViewportPaddingClass = shortViewportBehavior?.contentPaddingClass ?? 'pb-16';
  const [isShortViewport, setIsShortViewport] = useState(false);
  useEffect(() => {
    if (!shortViewportEnabled) {
      return undefined;
    }
    const updateViewportFlag = () => {
      if (typeof window === 'undefined') {
        return;
      }
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight ?? document.documentElement?.clientHeight ?? 0;
      if (!viewportHeight) {
        setIsShortViewport(false);
        return;
      }
      setIsShortViewport(viewportHeight <= shortViewportBreakpoint);
    };
    updateViewportFlag();
    window.addEventListener('resize', updateViewportFlag);
    window.addEventListener('orientationchange', updateViewportFlag);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', updateViewportFlag);
    return () => {
      window.removeEventListener('resize', updateViewportFlag);
      window.removeEventListener('orientationchange', updateViewportFlag);
      vv?.removeEventListener('resize', updateViewportFlag);
    };
  }, [shortViewportEnabled, shortViewportBreakpoint]);
  const useShortViewportLayout = shortViewportEnabled && isShortViewport;
  const resolvedGridColumns = (() => {
    if (!gridLayout) return null;
    const fallback = 4;
    const desired = useShortViewportLayout ? shortViewportColumns : fallback;
    return GRID_COLUMN_CLASSNAMES[desired] || GRID_COLUMN_CLASSNAMES[fallback];
  })();
  const optionsLayoutClasses = gridLayout
    ? `grid ${resolvedGridColumns} gap-3`
    : 'flex flex-col space-y-3';
  const resolvedContentWrapperClassName = useShortViewportLayout ? shortViewportPaddingClass : '';
  const resolvedContentJustify = useShortViewportLayout ? 'start' : 'center';

  const deriveInitialState = useCallback(() => {
    if (multiselect) {
      const rawValues = Array.isArray(selectedValues)
        ? [...selectedValues]
        : (selectedValues ? [selectedValues] : []);
      let derivedOtherValue = '';
      const normalizedSelections = rawValues.map((value) => {
        if (otherOptionId && value && !optionsById.has(value)) {
          derivedOtherValue = value;
          return otherOptionId;
        }
        return value;
      });
      let sanitizedSelections = normalizedSelections.filter(Boolean);
      if (sanitizedSelections.length > 1) {
        const exclusiveSelections = sanitizedSelections.filter((id) => exclusiveSet.has(id));
        if (exclusiveSelections.length > 0) {
          sanitizedSelections = [exclusiveSelections[0]];
        }
      }
      return {
        selection: null,
        selections: sanitizedSelections,
        otherValue: derivedOtherValue,
        resolvedValue: sanitizedSelections
          .map((id) => (otherOptionId && id === otherOptionId ? derivedOtherValue : id))
          .filter((value) => value !== null && value !== undefined && value !== ''),
      };
    }

    const value = selectedValue ?? null;
    if (otherOptionId && value && !optionsById.has(value)) {
      return {
        selection: otherOptionId,
        selections: [],
        otherValue: value,
        resolvedValue: value,
      };
    }

    return {
      selection: value,
      selections: [],
      otherValue: '',
      resolvedValue: value,
    };
  }, [multiselect, selectedValues, selectedValue, otherOptionId, optionsById, exclusiveSet]);

  const initialState = useMemo(() => deriveInitialState(), [deriveInitialState, resolvedFieldName]);

  const [localSelection, setLocalSelection] = useState(initialState.selection);
  const [localSelections, setLocalSelections] = useState(initialState.selections);
  const [otherValue, setOtherValue] = useState(initialState.otherValue);

  const initialSerializedValue = JSON.stringify(
    multiselect ? (initialState.resolvedValue ?? []) : (initialState.resolvedValue ?? null)
  );
  const lastCommittedRef = useRef({
    field: resolvedFieldName,
    serialized: initialSerializedValue,
  });
  const lastResolvedFromPropsRef = useRef(initialSerializedValue);

  const areArraysEqual = useCallback((a = [], b = []) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }, []);

  useEffect(() => {
    const derived = deriveInitialState();
    const serialized = JSON.stringify(
      multiselect ? (derived.resolvedValue ?? []) : (derived.resolvedValue ?? null)
    );

    if (serialized === lastResolvedFromPropsRef.current) {
      return;
    }

    lastResolvedFromPropsRef.current = serialized;

    if (multiselect) {
      if (!areArraysEqual(derived.selections, localSelections)) {
        setLocalSelections(derived.selections);
      }
    } else if (derived.selection !== localSelection) {
      setLocalSelection(derived.selection);
    }

    if (derived.otherValue !== otherValue) {
      setOtherValue(derived.otherValue);
    }

    lastCommittedRef.current = {
      field: resolvedFieldName,
      serialized,
    };
  }, [deriveInitialState, multiselect, resolvedFieldName, areArraysEqual, localSelections, localSelection, otherValue]);

  const resolveSingleValue = useCallback((selection, otherVal) => {
    if (!selection) return null;
    if (otherOptionId && selection === otherOptionId) {
      return otherVal && otherVal.length > 0 ? otherVal : null;
    }
    return selection;
  }, [otherOptionId]);

  const resolveMultiValues = useCallback((selections, otherVal) => {
    if (!Array.isArray(selections)) return [];
    return selections
      .map((id) => {
        if (otherOptionId && id === otherOptionId) {
          return otherVal && otherVal.length > 0 ? otherVal : null;
        }
        return id;
      })
      .filter((value) => value !== null && value !== undefined && value !== '');
  }, [otherOptionId]);

  const commitAutoSave = useCallback((value) => {
    if (!onAutoSave || !resolvedFieldName) {
      return;
    }
    const normalizedValue = multiselect
      ? (Array.isArray(value) ? value : [])
      : value;
    const serialized = JSON.stringify(normalizedValue ?? (multiselect ? [] : null));
    const lastCommitted = lastCommittedRef.current;
    if (
      lastCommitted &&
      lastCommitted.field === resolvedFieldName &&
      lastCommitted.serialized === serialized
    ) {
      return;
    }
    lastCommittedRef.current = {
      field: resolvedFieldName,
      serialized,
    };
    onAutoSave({ [resolvedFieldName]: normalizedValue });
  }, [onAutoSave, resolvedFieldName, multiselect]);

  const isOtherSelected = multiselect
    ? (otherOptionId ? localSelections.includes(otherOptionId) : false)
    : (otherOptionId ? localSelection === otherOptionId : false);

  const handleOptionSelectSingle = (optionId) => {
    setLocalSelection(optionId);
    if (otherOptionId && optionId !== otherOptionId) {
      setOtherValue('');
    }

    if (!otherOptionId || optionId !== otherOptionId) {
      commitAutoSave(resolveSingleValue(optionId, ''));
    }

    if (autoAdvance && !(otherOptionId && optionId === otherOptionId)) {
      setTimeout(() => onNext({ [resolvedFieldName]: resolveSingleValue(optionId, '') }), 250);
    }
  };

  const handleOptionToggleMulti = (optionId) => {
    setLocalSelections((prev) => {
      const exists = prev.includes(optionId);
      let next;

      if (exists) {
        next = prev.filter((id) => id !== optionId);
      } else if (exclusiveSet.has(optionId)) {
        next = [optionId];
      } else {
        const withoutExclusive = prev.filter((id) => !exclusiveSet.has(id));
        next = [...withoutExclusive, optionId];
        if (next.length > maxSelections) {
          next = next.slice(next.length - maxSelections);
        }
      }

      if (otherOptionId && !next.includes(otherOptionId)) {
        setOtherValue('');
      }

      const otherSelectedInNext = otherOptionId && next.includes(otherOptionId);
      if (!otherSelectedInNext || otherValue) {
        const resolved = resolveMultiValues(next, otherValue);
        commitAutoSave(resolved);
      }

      return next;
    });
  };

  const handleContinue = () => {
    if (multiselect) {
      if (localSelections.length < minSelections) return;
      if (isOtherSelected && !otherValue) return;

      const values = resolveMultiValues(localSelections, otherValue);
      onNext({ [resolvedFieldName]: values });
      return;
    }

    if (!localSelection) return;
    if (isOtherSelected && !otherValue) return;

    const value = resolveSingleValue(localSelection, otherValue);
    onNext({ [resolvedFieldName]: value });
  };

  useEffect(() => {
    if (!onAutoSave || !resolvedFieldName) return;

    if (multiselect) {
      if (otherOptionId && localSelections.includes(otherOptionId) && !otherValue) {
        return;
      }
      const resolved = resolveMultiValues(localSelections, otherValue);
      commitAutoSave(resolved);
      return;
    }

    if (!localSelection) {
      commitAutoSave(null);
      return;
    }

    if (otherOptionId && localSelection === otherOptionId && !otherValue) {
      return;
    }

    const resolved = resolveSingleValue(localSelection, otherValue);
    commitAutoSave(resolved);
  }, [multiselect, localSelections, localSelection, otherValue, otherOptionId, resolveMultiValues, resolveSingleValue, commitAutoSave, onAutoSave, resolvedFieldName]);

  const disabled = multiselect
    ? (localSelections.length < minSelections) || (isOtherSelected && !otherValue)
    : (!localSelection) || (isOtherSelected && !otherValue);

  const normalizeList = useCallback((arr) =>
    arr.map((item) => {
      if (typeof item === 'string') return { value: item, label: item };
      if (item.label && item.value) return item;
      if (item.name && item.code) return { label: item.name, value: item.name };
      if (item.name) return { label: item.name, value: item.name };
      return { label: String(item), value: String(item) };
    }), []);

  const normalizedOtherList = normalizeList(otherOptionsList);
  const optionsGrid = (
    <div className={`w-full mx-auto ${optionsLayoutClasses}`}>
      {options.map((option) => {
        const selected = multiselect
          ? localSelections.includes(option.id)
          : localSelection === option.id;

        return (
          <OptionButton
            key={option.id}
            label={option.label}
            icon={option.icon}
            selected={selected}
            onClick={() =>
              multiselect ? handleOptionToggleMulti(option.id) : handleOptionSelectSingle(option.id)
            }
            gridLayout={gridLayout}
            tintable={!!option.tintable}
            iconContainerClass={option.iconContainerClass ?? optionIconContainerClass}
            iconSizeClass={option.iconSizeClass ?? optionIconSizeClass}
            className={option.className}
          />
        );
      })}
    </div>
  );

  const otherInput = isOtherSelected ? (
    <div className={`${shortViewportEnabled ? 'w-full' : 'mt-4 w-full'}`}>
      {typeof renderOtherOption === 'function' ? (
        renderOtherOption({ value: otherValue, setValue: setOtherValue })
      ) : (
        <label>
          <select
            className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
            value={otherValue}
            onChange={(e) => setOtherValue(e.target.value)}
          >
            <option value="">{otherPlaceholder}</option>
            {normalizedOtherList.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  ) : null;

  const slideBody = shortViewportEnabled ? (
    <div className={`${useShortViewportLayout ? 'space-y-4 pb-4' : 'space-y-4'}`}>
      {optionsGrid}
      {otherInput}
    </div>
  ) : (
    <>
      {optionsGrid}
      {otherInput}
    </>
  );

  return (
    <SlideShell
      title={title}
      subtitle={subtitle}
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleContinue}
      onBack={onBack}
      nextLabel="Continue"
      disabled={disabled}
      animationDirection={animationDirection}
      isTransitioning={isTransitioning}
      scrollable={useShortViewportLayout}
      contentJustify={resolvedContentJustify}
      contentWrapperClassName={resolvedContentWrapperClassName}
    >
      {slideBody}
    </SlideShell>
  );
}
